/**
 * Share flow. HARD RULES (IDEA.md / CLAUDE.md):
 *   - The card IMAGE is the post. Never put a URL in the post body.
 *   - Do NOT use X's share-intent (it injects the link into the body).
 *   - The link ("play it: reflex.karrtopelka.com") is for the FIRST REPLY only.
 *   - Provocations only — no "follow/RT/tag to enter" engagement farming.
 *
 * Two surfaces, picked by capability:
 *   - MOBILE (Web Share API w/ files): one tap opens the native share sheet with the
 *     card PNG + the provocation as the post text (NO url — link stays reply-only). The
 *     OS routes it straight to X/IG/etc. This is the primary mobile path.
 *   - DESKTOP (clipboard): copy ONLY the image so cmd/ctrl+V pastes the card; provocation
 *     + reply link are offered separately with their own copy buttons.
 */

export interface SharePayload {
  /** Rendered result-card image. */
  png: Blob;
  /** Provocation with a concrete target, e.g. "147ms. bet none of you beat it". */
  provocation: string;
  /** Reply-only link, e.g. "reflex.karrtopelka.com". Never goes in the post body. */
  replyLink: string;
}

export type ShareCapability = "native" | "clipboard" | "download";

export interface ShareResult {
  /** Which path actually ran. */
  via: ShareCapability;
  /** True if the image reached its destination (sheet opened / clipboard written). */
  ok: boolean;
  /** User dismissed the native share sheet — not an error, don't show a fallback. */
  cancelled?: boolean;
}

const CARD_FILENAME = "reflex.png";

function toFile(png: Blob): File {
  return new File([png], CARD_FILENAME, { type: "image/png" });
}

/**
 * Best available share path for THIS device. Computed from feature detection:
 * - "native" when the Web Share API can share files (mobile Safari 16.4+ / Android).
 * - "clipboard" when ClipboardItem image write exists (desktop Chrome/Edge/Safari).
 * - "download" otherwise (last-resort save).
 *
 * Must be called with a real File to probe (navigator.canShare checks the payload).
 */
export function shareCapability(png: Blob): ShareCapability {
  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [toFile(png)] })
    ) {
      return "native";
    }
  } catch {
    /* fall through */
  }
  // Runtime guard (not just the typed surface): clipboard / ClipboardItem are absent in
  // insecure contexts and some mobile browsers even though the lib types them as present.
  if (
    typeof ClipboardItem !== "undefined" &&
    typeof navigator.clipboard !== "undefined" &&
    "write" in navigator.clipboard
  ) {
    return "clipboard";
  }
  return "download";
}

/** True if this device should lead with the native share sheet. */
export function canShareNative(png: Blob): boolean {
  return shareCapability(png) === "native";
}

async function copyImage(png: Blob): Promise<boolean> {
  try {
    if (
      typeof ClipboardItem === "undefined" ||
      typeof navigator.clipboard === "undefined" ||
      !("write" in navigator.clipboard)
    ) {
      return false;
    }
    // Some Safari builds require the ClipboardItem value be a Promise<Blob>; passing the
    // blob directly also works on Chromium. Promise form is the broader-compatible one.
    const item = new ClipboardItem({ "image/png": Promise.resolve(png) });
    await navigator.clipboard.write([item]);
    return true;
  } catch {
    // Retry once with the plain-blob form for engines that reject the Promise form.
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
      return true;
    } catch {
      return false;
    }
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (
      typeof navigator.clipboard === "undefined" ||
      !("writeText" in navigator.clipboard)
    ) {
      return false;
    }
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open the native share sheet with the card image + the provocation as the post text.
 * The text is the provocation ONLY (no URL — the link stays for the reply). Returns
 * cancelled:true if the user dismissed the sheet (so the UI doesn't show a fallback).
 */
export async function shareCardNative(payload: SharePayload): Promise<ShareResult> {
  const file = toFile(payload.png);
  try {
    await navigator.share({
      files: [file],
      // No `url` field — that would put a link in the share. Image + provocation only.
      text: payload.provocation,
    });
    return { via: "native", ok: true };
  } catch (e) {
    // AbortError = user closed the sheet. Treat as a clean cancel, not a failure.
    if (e instanceof DOMException && e.name === "AbortError") {
      return { via: "native", ok: false, cancelled: true };
    }
    return { via: "native", ok: false };
  }
}

/**
 * Copy the card image to the clipboard — and ONLY the image, so cmd/ctrl+V pastes the
 * card (the image IS the post). Provocation + reply link are surfaced separately in the
 * UI (writing text here would clobber the image — only one item survives the last write).
 */
export async function shareCard(payload: SharePayload): Promise<ShareResult> {
  const ok = await copyImage(payload.png);
  return { via: "clipboard", ok };
}

/** Copy the provocation text on its own (separate UI action — never alongside the image). */
export async function copyProvocation(text: string): Promise<boolean> {
  return copyText(text);
}

/** Trigger a PNG download as the universal fallback. (On iOS Safari this opens the
 * image in a new tab rather than saving — acceptable last resort; native/clipboard
 * are preferred and chosen first.) */
export function downloadCard(png: Blob, filename: string = CARD_FILENAME): void {
  const url = URL.createObjectURL(png);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
